import {
  AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, QueryList, SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { NgModel } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { merge, Subscription } from 'rxjs';
import { minNotAboveMax, RangeRule } from '../../utils/range-rules';

/** Instance counter for the error container's id, so two ranges on one page never share it. */
let nextRangeId = 0;

// g15-i18n-keys
const CONTROL_ERROR_KEYS = {
  notANumber: 'validation.number',
} as const;

/**
 * Reusable numeric range filter: a coupled min/max pair for filtering a numeric field.
 * Two-way bind both bounds — `[(min)]` / `[(max)]`. Validation: min ≤ max always, then any `rules`
 * (see `shared/utils/range-rules.ts`); every failing rule shows its own message, and `(validChange)`
 * reports the verdict on every change (and once at init). The inputs also constrain each other
 * (`[max]`/`[min]`) so an impossible range is hard to enter in the first place.
 *
 * Example: `<app-number-range label="filter.items" [(min)]="r.min" [(max)]="r.max"></app-number-range>`
 */
@Component({
  selector: 'app-number-range',
  templateUrl: './number-range.component.html',
  styleUrls: ['./number-range.component.scss'],
})
export class NumberRangeComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  /** The error container's id, referenced by both inputs' aria-describedby while there are errors. */
  readonly errorsId = `g15-number-range-errors-${nextRangeId++}`;
  /** The two fields' controls: text appNumberInput cannot parse lands as a null model plus a
   *  `number` error, which the numeric rules alone cannot see. */
  @ViewChildren(NgModel) private models?: QueryList<NgModel>;
  /** Drives both inputs' error state off the RANGE's verdict, so MatInput (which owns
   *  `aria-invalid`) and the form field paint the same invalidity the messages below describe. */
  readonly errorStateMatcher: ErrorStateMatcher = { isErrorState: () => this.invalid };
  private statusSub?: Subscription;

  /** i18n key for the range's name shown above the two fields. */
  @Input() label = '';
  @Input() min: number | null = null;
  @Output() minChange = new EventEmitter<number | null>();
  @Input() max: number | null = null;
  @Output() maxChange = new EventEmitter<number | null>();
  /** `integer` for counts and quantities; `decimal` (the default) accepts fractions. */
  @Input() mode: 'integer' | 'decimal' = 'decimal';
  /** Rounds typed decimals to this many fraction digits; null (the default) leaves them alone. */
  @Input() maxDecimals: number | null = null;
  /** A lower bound for BOTH fields (e.g. 0 for a quantity); null (the default) sets none. */
  @Input() floor: number | null = null;
  /** i18n keys of the two fields' labels. */
  @Input() minLabel = 'common.min';
  @Input() maxLabel = 'common.max';

  /** The max field's lower bound: the min entered, never below the floor. */
  get maxFieldMin(): number | null {
    if (this.floor == null) { return this.min; }
    if (this.min == null) { return this.floor; }
    return Math.max(this.floor, this.min);
  }

  /** Extra rules, run after the built-in min ≤ max. None by default. */
  @Input() rules: RangeRule[] = [];
  /** The range's validity, emitted on every change and once at init. */
  @Output() validChange = new EventEmitter<boolean>();

  /** Every failing rule's i18n key, the built-in one first, then unparseable input (once). */
  get errors(): string[] {
    const range = { min: this.min, max: this.max };
    const keys = [minNotAboveMax, ...this.rules]
      .map(rule => rule(range))
      .filter((key): key is string => key != null);
    // Only the `number` error is folded in: the directive's min/max errors restate the rules above.
    if (this.models?.some(m => m.control.hasError('number'))) {
      keys.push(CONTROL_ERROR_KEYS.notANumber);
    }
    return keys;
  }

  get invalid(): boolean {
    return this.errors.length > 0;
  }

  ngOnInit(): void {
    this.emitValidity();
  }

  ngAfterViewInit(): void {
    // A control's validity can change without a model change (text typed over an already-null
    // model, a host write landing asynchronously), so the verdict follows the controls too.
    const models = this.models?.toArray() ?? [];
    this.statusSub = merge(...models.map(m => m.control.statusChanges)).subscribe(() => this.emitValidity());
  }

  ngOnDestroy(): void {
    this.statusSub?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const later = Object.values(changes).some(c => !c.firstChange);
    if (later && (changes['min'] || changes['max'] || changes['rules'])) {
      this.emitValidity();
    }
  }

  /**
   * Empties both fields, including text the number input could not parse. A host's Reset alone
   * cannot: such text leaves the bound model null, so writing null again is no binding change and
   * NgModel never rewrites the input — the junk text and its `number` error would stay.
   */
  reset(): void {
    this.min = null;
    this.max = null;
    // FormControl.reset writes null through the value accessor (clearing the directive's
    // invalid-text state) and re-runs the validators.
    this.models?.forEach(m => m.control.reset(null));
    this.emitValidity();
  }

  private emitValidity(): void {
    this.validChange.emit(!this.invalid);
  }

  onMin(value: number | null): void {
    this.min = value;
    this.minChange.emit(value);
    this.emitValidity();
  }

  onMax(value: number | null): void {
    this.max = value;
    this.maxChange.emit(value);
    this.emitValidity();
  }
}
